'use client';

import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { LucideIcon } from '@skin-seoul/design-system';
import { ImpressionArea } from '@skin-seoul/utils';
import { useCartAnalytics } from './CartAnalytics';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import { useCartDiscovery } from './CartDiscoveryNavigation';
import {
  CLASSNAME,
  formatCartCurrency,
  formatCartMilestoneCurrency,
  getCartMilestoneStates,
  useCartBenefitCelebration,
  useCartService,
  useRegionCurrency,
  useUser,
  type Currency,
} from '@skin-seoul/service';
import { getCartBenefits, hasAllCartRewards, type CartBenefit } from './cartBenefits';
import { useCartBenefitsTracking } from './useCartBenefitsTracking';
import { CartBenefitsGradient } from './CartBenefitsGradient';

const CART_BENEFITS_TEST_ID = 'bmsm-banner';
const IMPRESSION_AREA_THRESHOLD = 0.5;
const IMPRESSION_TIME_THRESHOLD_MS = 1000;
const COPY_TRANSITION_SECONDS = 0.18;
const COPY_OFFSET_PX = 4;

interface CartBenefitsBannerProps {
  className?: string;
  isTrackingActive: boolean;
  layout?: 'empty' | 'populated';
  onRewardClick?: () => void;
  uiLocation?: 'cart_drawer' | 'unlock_reward_page';
}

function BenefitCopy({
  benefits,
  celebration,
  currency,
  layout,
}: {
  layout: 'empty' | 'populated';
  benefits: CartBenefit[];
  celebration: CartBenefit | null;
  currency: Currency;
}) {
  const nextBenefit = benefits.find(({ available }) => !available);
  if (!celebration && nextBenefit) {
    return (
      <span className="flex flex-wrap items-center gap-x-1">
        <span>Add</span>{' '}
        <strong className={`${layout === 'empty' ? 'text-paragraph-medium' : ''} font-semibold text-success-500`}>
          {formatCartCurrency(currency, Math.max(0, nextBenefit.remaining))}
        </strong>{' '}
        <span>
          {(() => {
            if (!(nextBenefit.currentAmount > 0)) {
              return 'to get';
            }
            if (nextBenefit.id === 'shipping') {
              return (
                <>
                  <span className="desktop:hidden">to get</span>
                  <span className="hidden desktop:inline">more to get</span>
                </>
              );
            }
            return 'more to get';
          })()}
        </span>{' '}
        <strong className="text-paragraph-medium font-semibold text-success-500">{nextBenefit.title}</strong>
      </span>
    );
  }
  const unlocked = celebration ?? benefits.filter(({ available }) => available).at(-1);
  if (!unlocked) {
    return <>You reached the reward goals.</>;
  }
  return (
    <>
      <strong className="text-paragraph-medium font-semibold text-success-500">{unlocked.title}</strong> unlocked!
      {hasAllCartRewards(benefits) && ' You got it all!'}
    </>
  );
}

function MilestoneLabel({
  benefit,
  isNext,
  allRewardsReached,
  currency,
}: {
  benefit: CartBenefit;
  isNext: boolean;
  allRewardsReached: boolean;
  currency: Currency;
}) {
  const isReached = benefit.available;
  const labelRef = useRef<HTMLSpanElement>(null);
  const measurementRef = useRef<HTMLSpanElement>(null);
  const [hasStackedLabels, setHasStackedLabels] = useState(false);
  const threshold = formatCartMilestoneCurrency(currency, benefit.minAmount);
  const inlineLabel = isReached ? benefit.label : `${threshold} · ${benefit.label}`;

  useLayoutEffect(() => {
    const label = labelRef.current;
    const measurement = measurementRef.current;
    if (!label || !measurement || isReached) {
      return;
    }
    const measure = () => {
      const style = getComputedStyle(label);
      const availableWidth = label.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (availableWidth > 0) {
        setHasStackedLabels(measurement.getBoundingClientRect().width > availableWidth);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    observer.observe(measurement);
    return () => observer.disconnect();
  }, [inlineLabel, isReached]);
  const color = (() => {
    if (isReached) {
      return allRewardsReached && benefit.id !== 'shipping' ? 'text-neutral-200' : 'text-success-700';
    }
    if (isNext) {
      return 'text-generic-0 font-semibold';
    }
    return 'text-neutral-400';
  })();
  const isStacked = hasStackedLabels && !isReached;
  return (
    <span
      ref={labelRef}
      className={`relative flex min-w-0 items-center gap-0.5 font-medium ${isStacked ? 'text-label-xsmall' : 'text-label-small'} ${color}`}
    >
      {!isReached && (
        <span
          ref={measurementRef}
          aria-hidden="true"
          className="pointer-events-none invisible absolute w-max whitespace-nowrap text-label-small"
        >
          {inlineLabel}
        </span>
      )}
      {isReached && allRewardsReached && <LucideIcon name="Check" size={12} className="shrink-0" />}
      {isStacked ? (
        <span className="flex min-w-0 flex-col whitespace-nowrap">
          <span>{threshold}</span>
          <span>{benefit.label}</span>
        </span>
      ) : (
        <span className="whitespace-nowrap">{inlineLabel}</span>
      )}
    </span>
  );
}

export function CartBenefitsBanner({
  isTrackingActive,
  layout = 'populated',
  className = '',
  onRewardClick,
  uiLocation = 'cart_drawer',
}: CartBenefitsBannerProps) {
  const analytics = useCartAnalytics();
  const { isOpen } = useCartDrawer();
  const { isDetailOpen } = useCartDiscovery();
  const trackingActive =
    isTrackingActive && isOpen && (uiLocation === 'unlock_reward_page' ? Boolean(isDetailOpen) : !isDetailOpen);
  const { currency, country } = useRegionCurrency();
  const shouldReduceMotion = useReducedMotion();
  const { user, isAuthenticated } = useUser();
  const { useGetCartSuspenseQuery } = useCartService();
  const { data } = useGetCartSuspenseQuery({ currency });
  const progressBar = data.cart.progressBar ?? null;
  const { benefits, anchors, progress, campaignKey } = getCartBenefits(progressBar, currency);
  const identity = JSON.stringify([country, currency, user?.userId, isAuthenticated, isTrackingActive, campaignKey]);
  const celebration = useCartBenefitCelebration(benefits, identity);
  const next = benefits.find(({ available }) => !available);
  const reached = celebration ?? benefits.filter(({ available }) => available).at(-1);
  const headline =
    celebration || !next
      ? `${reached?.title ?? 'Reward goals'} unlocked!${hasAllCartRewards(benefits) ? ' You got it all!' : ''}`
      : `Add ${formatCartCurrency(currency, Math.max(0, next.remaining))} ${next.currentAmount > 0 ? 'more to get' : 'to get'} ${next.title}`;
  const { handleImpressionStart, handleImpressionEnd } = useCartBenefitsTracking(progressBar, trackingActive, {
    cart: data.cart,
    headline,
    uiLocation,
  });
  if (benefits.length === 0) {
    return null;
  }

  const allRewardsReached = hasAllCartRewards(benefits);
  const isVisibleMilestone = (benefit: CartBenefit, index: number) =>
    !allRewardsReached || benefit.id === 'shipping' || index === benefits.length - 1;
  const nextId = benefits.find(({ available }) => !available)?.id;
  const milestones = getCartMilestoneStates(benefits);
  const bannerHeight = layout === 'empty' ? 'min-h-[88px]' : 'min-h-[89px]';
  const copyKey = JSON.stringify([
    celebration?.id,
    benefits.map(({ remaining, available }) => [remaining, available]),
    currency,
  ]);
  const content: ReactNode = (
    <div
      data-testid={CART_BENEFITS_TEST_ID}
      className={`relative flex w-full flex-col gap-0.5 overflow-hidden rounded-2xl bg-generic-100 px-3 pt-[9px] ${layout === 'empty' ? 'pb-[13px]' : 'pb-[14px]'} ${bannerHeight} ${CLASSNAME.NOT_TRANSLATE} ${className}`}
    >
      <CartBenefitsGradient />
      <div aria-live="polite" aria-atomic="true" className={`relative min-h-6 ${onRewardClick ? 'pr-6' : ''}`}>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.p
            key={copyKey}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : COPY_OFFSET_PX }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -COPY_OFFSET_PX }}
            transition={{ duration: shouldReduceMotion ? 0 : COPY_TRANSITION_SECONDS }}
            className="relative text-paragraph-small font-medium text-neutral-50"
          >
            <BenefitCopy benefits={benefits} celebration={celebration} currency={currency} layout={layout} />
          </motion.p>
        </AnimatePresence>
        {onRewardClick && !allRewardsReached && (
          <button
            type="button"
            aria-label="Unlock reward"
            onClick={() => {
              analytics.track('bmsm_bar_headline_clicked', { headline }, uiLocation);
              onRewardClick();
            }}
            className="absolute -right-0.5 top-0 flex size-6 items-center justify-center rounded-sm text-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-success-500"
          >
            <LucideIcon name="ChevronRight" size={20} strokeWidth={1.8} className="text-neutral-75" />
          </button>
        )}
      </div>
      <div
        role="progressbar"
        aria-label="Cart benefits"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
        className="relative h-[18px] w-[calc(100%-1px)] shrink-0"
      >
        <div className="absolute inset-x-0 top-[7px] h-1 rounded-sm bg-generic-0/20">
          <div
            className="h-full rounded-sm bg-success-500 transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>
        {benefits.map((benefit, index) => {
          if (!isVisibleMilestone(benefit, index)) {
            return null;
          }
          const { reached, current } = milestones[index];
          const pinStyle = {
            left: `calc(${anchors[index]}% - ${current ? 0.5 : 1}px)`,
            transform: index === benefits.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
          };
          return (
            <Fragment key={benefit.id}>
              {current ? (
                <span
                  style={pinStyle}
                  className="absolute top-0 flex size-[18px] items-center justify-center rounded-full bg-success-500"
                >
                  <LucideIcon name="Check" size={10} strokeWidth={2.5} className="text-generic-0" />
                </span>
              ) : (
                <span
                  style={pinStyle}
                  className={`absolute top-[5px] size-2 rounded-full ${reached ? 'bg-success-500' : 'bg-neutral-300'}`}
                />
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="relative flex w-[calc(100%-1px)] items-start pt-1 [&>span]:shrink-0 [&>span:first-child]:w-[37.3%] [&>span:first-child]:justify-center [&>span:first-child]:pr-3.5 [&>span:not(:first-child):not(:last-child)]:w-[30%] [&>span:not(:first-child):not(:last-child)]:justify-center [&>span:last-child:not(:first-child)]:flex-1 [&>span:last-child:not(:first-child)]:justify-end">
        {benefits.filter(isVisibleMilestone).map((benefit) => (
          <MilestoneLabel
            key={benefit.id}
            benefit={benefit}
            currency={currency}
            isNext={benefit.id === nextId}
            allRewardsReached={allRewardsReached}
          />
        ))}
      </div>
    </div>
  );

  if (!trackingActive) {
    return content;
  }
  return (
    <ImpressionArea
      areaThreshold={IMPRESSION_AREA_THRESHOLD}
      timeThreshold={IMPRESSION_TIME_THRESHOLD_MS}
      onImpressionStart={handleImpressionStart}
      onImpressionEnd={handleImpressionEnd}
    >
      {content}
    </ImpressionArea>
  );
}
